import type { JudoRestApi } from "@judo/actions";
import type { CustomDialogProps } from "@judo/feedback";
import type { RelationType } from "@judo/model-api";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
	Box,
	Button,
	CircularProgress,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface TransferStored {
	__identifier?: string;
	__signedIdentifier?: string;
	[key: string]: unknown;
}

type ItemStatus = "pending" | "running" | "success" | "error";

interface ItemState {
	status: ItemStatus;
	error?: string;
}

export interface BulkRemoveProgressDialogProps extends CustomDialogProps<boolean> {
	items: TransferStored[];
	api: JudoRestApi;
	relation: RelationType;
	ownerTransfer: TransferStored;
	/** Attribute name to display as item label (e.g. first column's attributeType.name) */
	displayField?: string;
}

function getItemId(item: TransferStored, index: number): string {
	return item.__signedIdentifier ?? item.__identifier ?? `__index_${index}`;
}

function getItemLabel(item: TransferStored, displayField: string | undefined, index: number): string {
	if (displayField && item[displayField] != null && item[displayField] !== "") {
		return String(item[displayField]);
	}
	if (item.__identifier) {
		return item.__identifier;
	}
	return `Item ${index + 1}`;
}

type Phase = "confirm" | "running" | "done";

/**
 * Combined confirmation + progress dialog for bulk remove operations on lazy tables.
 * First shows the list of items with a confirm button, then removes items one by one
 * showing per-item success/error status.
 */
export function BulkRemoveProgressDialog({
	onClose,
	items,
	api,
	relation,
	ownerTransfer,
	displayField,
}: BulkRemoveProgressDialogProps) {
	const { t } = useTranslation();
	const [phase, setPhase] = useState<Phase>("confirm");
	const [itemStates, setItemStates] = useState<Map<string, ItemState>>(() => {
		const map = new Map<string, ItemState>();
		for (let i = 0; i < items.length; i++) {
			map.set(getItemId(items[i], i), { status: "pending" });
		}
		return map;
	});
	const [completed, setCompleted] = useState(0);
	const [hasErrors, setHasErrors] = useState(false);
	const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
		};
	}, []);

	const handleConfirm = useCallback(() => {
		setPhase("running");

		async function processItems() {
			let errorCount = 0;

			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				const itemId = getItemId(item, i);

				setItemStates((prev) => {
					const next = new Map(prev);
					next.set(itemId, { status: "running" });
					return next;
				});

				try {
					await api.removeFromRelation(relation, ownerTransfer as any, [item as any]);
					setItemStates((prev) => {
						const next = new Map(prev);
						next.set(itemId, { status: "success" });
						return next;
					});
				} catch (error) {
					errorCount++;
					setItemStates((prev) => {
						const next = new Map(prev);
						next.set(itemId, {
							status: "error",
							error: error instanceof Error ? error.message : "Unknown error",
						});
						return next;
					});
				}

				setCompleted(i + 1);
			}

			setPhase("done");
			if (errorCount > 0) {
				setHasErrors(true);
			} else {
				autoCloseTimer.current = setTimeout(() => {
					onClose(true);
				}, 1000);
			}
		}

		processItems();
	}, [items, api, relation, ownerTransfer, onClose]);

	const progress = items.length > 0 ? (completed / items.length) * 100 : 0;

	const title =
		phase === "confirm"
			? t("system.bulkRemove.title.confirm", {
					defaultValue: "Remove {{count}} items?",
					count: items.length,
				})
			: phase === "running"
				? t("system.bulkRemove.title.running", { defaultValue: "Removing items..." })
				: hasErrors
					? t("system.bulkRemove.title.errors", { defaultValue: "Removal completed with errors" })
					: t("system.bulkRemove.title.complete", { defaultValue: "Removal complete" });

	return (
		<>
			<DialogTitle>{title}</DialogTitle>
			<DialogContent dividers>
				{phase !== "confirm" && (
					<Box sx={{ mb: 2 }}>
						<LinearProgress variant="determinate" value={progress} />
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
							{t("system.bulkRemove.progress", {
								defaultValue: "{{completed}} of {{total}}",
								completed,
								total: items.length,
							})}
						</Typography>
					</Box>
				)}
				<List dense disablePadding>
					{items.map((item, index) => {
						const itemId = getItemId(item, index);
						const state = itemStates.get(itemId);
						const label = getItemLabel(item, displayField, index);

						return (
							<ListItem key={itemId} disableGutters>
								{phase !== "confirm" && (
									<ListItemIcon sx={{ minWidth: 36 }}>
										<StatusIcon status={state?.status ?? "pending"} />
									</ListItemIcon>
								)}
								<ListItemText
									primary={label}
									secondary={state?.status === "error" ? state.error : undefined}
									secondaryTypographyProps={{ color: "error" }}
								/>
							</ListItem>
						);
					})}
				</List>
			</DialogContent>
			<DialogActions>
				{phase === "confirm" ? (
					<>
						<Button onClick={() => onClose(undefined)}>
							{t("system.action.cancel.label", { defaultValue: "Cancel" })}
						</Button>
						<Button onClick={handleConfirm} variant="contained" color="error">
							{t("system.bulkRemove.confirm", { defaultValue: "Remove All" })}
						</Button>
					</>
				) : (
					<Button onClick={() => onClose(true)} disabled={phase === "running"} variant="contained">
						{t("system.action.close.label", { defaultValue: "Close" })}
					</Button>
				)}
			</DialogActions>
		</>
	);
}

function StatusIcon({ status }: { status: ItemStatus }) {
	switch (status) {
		case "pending":
			return <CircularProgress size={20} sx={{ color: "action.disabled" }} variant="determinate" value={0} />;
		case "running":
			return <CircularProgress size={20} />;
		case "success":
			return <CheckCircleIcon color="success" fontSize="small" />;
		case "error":
			return <ErrorIcon color="error" fontSize="small" />;
	}
}
